import { supabase } from './supabase'
import type { MentionablePerson } from './mentions'

/**
 * 143 (FED-49). People in a feed photo.
 *
 * Keyed by storage path, like 141's contest box, because edit_post replaces a
 * post's attachment rows on every edit while the file stays the same.
 *
 *   * Only the UPLOADER tags (set_photo_tags), and only once the photo is on
 *     one of their posts — so the composer writes tags AFTER the post saves.
 *   * The tagged person, the uploader or a moderator removes a tag. Somebody who
 *     removed THEMSELVES cannot be tagged on that photo again.
 *   * A tagged person has their own contest-and-marketing say: in unless they
 *     say no (David, 9 Oct). Untagging does NOT change that answer — the two
 *     are separate on purpose, and a "no" outlives the tag.
 *
 * 161 (David, 11 Oct: "tag people directly in the photo with the tag pinned
 * to them"). A tag can carry a SPOT: x and y as fractions (0..1) of the photo's
 * width and height from its top-left corner. Optional, both or neither — a
 * name added from the list without a place is still a tag, and every tag from
 * before 161 has none. The reads and the write moved to photo_tags_at and
 * set_photo_tags_at; the old pair stays on the server for main until promote.
 */

/** A person in a photo, with where they are in it (161), or null if not placed. */
export type TaggedPerson = MentionablePerson & { x: number | null; y: number | null }

/** Clamp to the photo and keep four decimals, which is what the column holds. */
export function spot(v: number): number {
  return Math.round(Math.min(1, Math.max(0, v)) * 10000) / 10000
}

export type PhotoTag = {
  path: string
  personId: string
  displayName: string
  isMe: boolean
  canRemove: boolean
  /** Only on your own row: your contest-and-marketing answer (true = not objected). */
  myMayFeature: boolean | null
  /** 161. Where they are in the photo (0..1 each), or null when not placed. */
  x: number | null
  y: number | null
}

export type TagOutcome = 'tagged' | 'kept' | 'removed' | 'removed_themselves' | 'not_a_member' | 'cannot_see'

export async function photoTagsFor(paths: string[]): Promise<PhotoTag[]> {
  if (paths.length === 0) return []
  const { data, error } = await supabase.rpc('photo_tags_at', { p_paths: paths })
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => {
    // numeric arrives as a number or a string depending on the driver path.
    const x = r.x === null || r.x === undefined ? null : Number(r.x)
    const y = r.y === null || r.y === undefined ? null : Number(r.y)
    const placed = x !== null && y !== null && Number.isFinite(x) && Number.isFinite(y)
    return {
      path: r.storage_path as string,
      personId: r.person_id as string,
      displayName: r.display_name as string,
      isMe: r.is_me === true,
      canRemove: r.can_remove === true,
      myMayFeature: (r.my_may_feature as boolean | null) ?? null,
      x: placed ? x : null,
      y: placed ? y : null,
    }
  })
}

/** Sets a photo's whole tag list, with where each person is. Uploader only;
 *  the server refuses anyone else (143's rules, which 161 calls). */
export async function setPhotoTags(
  path: string,
  tags: TaggedPerson[],
): Promise<{ personId: string; outcome: TagOutcome }[]> {
  const { data, error } = await supabase.rpc('set_photo_tags_at', {
    p_storage_path: path,
    p_tags: tags.map((p) =>
      p.x !== null && p.y !== null
        ? { person_id: p.personId, x: spot(p.x), y: spot(p.y) }
        : { person_id: p.personId, x: null, y: null },
    ),
  })
  if (error) throw error
  return ((data ?? []) as { person_id: string; outcome: TagOutcome }[]).map((r) => ({
    personId: r.person_id,
    outcome: r.outcome,
  }))
}

/** Removes one tag. Omit personId to remove yourself. */
export async function removePhotoTag(path: string, personId?: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('remove_photo_tag', {
    p_storage_path: path,
    p_person_id: personId ?? null,
  })
  if (error) throw error
  return data === true
}

/**
 * 170. Every live photo you are or were tagged in, newest first (server caps
 * at 200) — Profile's list. NO per-photo box any more (David, 11 Oct: "it
 * should not be a toggle check box"): set_my_photo_choice is retired, and a
 * tagged person who does not want the company to use a photo asks for a
 * review on the post, inside the consent window (`consentUntil`).
 */
export type TaggedPhoto = {
  path: string
  contentId: string
  taggedByName: string
  taggedAt: string
  removedAt: string | null
  /** You posted it yourself (you tagged yourself): no review to ask for. */
  isMine: boolean
  /** Until when "I'm in it and don't consent" may be asked; null if unknown. */
  consentUntil: string | null
  consentOpen: boolean
  reviewOpen: boolean
  reviewNoConsent: boolean
}

export async function myTaggedPhotos(): Promise<TaggedPhoto[]> {
  const { data, error } = await supabase.rpc('my_tagged_photos')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    path: r.storage_path as string,
    contentId: r.content_id as string,
    taggedByName: r.tagged_by_name as string,
    taggedAt: r.tagged_at as string,
    removedAt: (r.removed_at as string | null) ?? null,
    isMine: r.is_mine === true,
    consentUntil: (r.consent_until as string | null) ?? null,
    consentOpen: r.consent_open === true,
    reviewOpen: r.review_open === true,
    reviewNoConsent: r.review_no_consent === true,
  }))
}

/* ── 163: a colleague suggests a tag; the poster or a moderator decides ──────
   A suggestion is invisible to the person suggested until it is accepted, and
   accepting it is 143's tag (same checks, same once-only bell). The server
   says who may suggest and who may decide; the screen never works it out. */

export type TagSuggestion = {
  id: string
  path: string
  personId: string
  displayName: string
  suggestedByName: string
  x: number | null
  y: number | null
  isMine: boolean
  canDecide: boolean
}

export type SuggestOutcome =
  | 'suggested'
  | 'already_tagged'
  | 'already_suggested'
  | 'removed_themselves'
  | 'not_a_member'
  | 'cannot_see'

export type SuggestRights = { maySuggest: boolean; mayDecide: boolean }

function spotOf(r: Record<string, unknown>): { x: number | null; y: number | null } {
  const x = r.x === null || r.x === undefined ? null : Number(r.x)
  const y = r.y === null || r.y === undefined ? null : Number(r.y)
  return x !== null && y !== null && Number.isFinite(x) && Number.isFinite(y) ? { x, y } : { x: null, y: null }
}

/** The waiting suggestions you may see: your own, or all of them if you decide. */
export async function suggestionsFor(paths: string[]): Promise<TagSuggestion[]> {
  if (paths.length === 0) return []
  const { data, error } = await supabase.rpc('photo_tag_suggestions_for', { p_paths: paths })
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.id as string,
    path: r.storage_path as string,
    personId: r.person_id as string,
    displayName: r.display_name as string,
    suggestedByName: r.suggested_by_name as string,
    ...spotOf(r),
    isMine: r.is_mine === true,
    canDecide: r.can_decide === true,
  }))
}

/** What you may do on each photo. Absent = nothing. */
export async function suggestRights(paths: string[]): Promise<Map<string, SuggestRights>> {
  const out = new Map<string, SuggestRights>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('photo_suggest_rights', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    out.set(r.storage_path as string, { maySuggest: r.may_suggest === true, mayDecide: r.may_decide === true })
  }
  return out
}

/** Suggest someone — or yourself, with personId null ("That's me"). */
export async function suggestPhotoTag(
  path: string,
  personId: string | null,
  at: { x: number; y: number } | null,
): Promise<SuggestOutcome> {
  const { data, error } = await supabase.rpc('suggest_photo_tag', {
    p_storage_path: path,
    p_person_id: personId,
    p_x: at ? spot(at.x) : null,
    p_y: at ? spot(at.y) : null,
  })
  if (error) throw error
  return data as SuggestOutcome
}

/** Accept or decline. 'accepted' / 'declined', or why it could not be done. */
export async function decideSuggestion(id: string, accept: boolean): Promise<string> {
  const { data, error } = await supabase.rpc('decide_photo_tag_suggestion', { p_id: id, p_accept: accept })
  if (error) throw error
  return data as string
}

/** Take back your own suggestion. */
export async function withdrawSuggestion(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('withdraw_photo_tag_suggestion', { p_id: id })
  if (error) throw error
  return data === true
}
